<!-- src/apps/workspace/components/account/AccountCard.vue -->

<script setup lang="ts">
  import OIcon from '@/shared/components/icons/OIcon.vue';

  /**
   * Card shell for the account surface (Figma Make "Conta", ago/2026).
   *
   * Every panel in the mock is the same object: a square-cornered white sheet
   * on the tinted page, hairline border, one soft drop shadow, and an optional
   * head strip (icon + title + subtitle) separated by a second hairline. It
   * lives here rather than being repeated per panel because four panels
   * (Perfil, Segurança, Chave de API, Zona de Atenção) draw it, and the
   * shadow/border pair is exactly the kind of thing that drifts when copied.
   *
   * `danger` is the mock's one variant: a blush ground and a red border, used
   * only by the account-closure panel.
   */
  withDefaults(
    defineProps<{
      icon?: { collection: string; name: string };
      title?: string;
      subtitle?: string;
      danger?: boolean;
    }>(),
    { icon: undefined, title: undefined, subtitle: undefined, danger: false }
  );
</script>

<template>
  <!-- `account-card` is a component-identity hook (no styling of its own):
       it lets callers and tests address the card shell without depending on
       whichever utility classes the current design happens to use. -->
  <div
    :class="[
      'account-card border shadow-[0_2px_10px_rgba(13,21,55,0.04)] dark:shadow-none',
      danger
        ? 'border-red-500/40 bg-red-50/40 dark:border-red-800/60 dark:bg-red-950/20'
        : 'border-day-line-2 bg-day dark:border-white/[0.13] dark:bg-night-100',
    ]">
    <div
      v-if="title"
      class="flex items-start gap-3.5 border-b border-day-line px-[22px] py-[18px] dark:border-white/[0.13]">
      <OIcon
        v-if="icon"
        :collection="icon.collection"
        :name="icon.name"
        :class="[
          'mt-px size-[18px] shrink-0',
          danger
            ? 'text-red-600 dark:text-red-400'
            : 'text-day-ink-muted dark:text-night-ink-muted',
        ]"
        aria-hidden="true" />
      <div class="min-w-0">
        <h2
          :class="[
            'mb-0 font-sans text-[15px] font-semibold',
            danger ? 'text-red-600 dark:text-red-400' : 'text-day-ink dark:text-night-ink',
          ]">
          {{ title }}
        </h2>
        <p
          v-if="subtitle"
          class="mt-0.5 text-[13px] text-day-ink-faint dark:text-night-ink-faint">
          {{ subtitle }}
        </p>
      </div>
    </div>

    <slot></slot>
  </div>
</template>
